import React, {
    useMemo,
    memo,
    useEffect,
    useLayoutEffect,
    useState,
    useReducer,
    useCallback,
    useRef,
    Fragment,
} from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, showToast, Button } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { getStandardField, updateStandardField } from 'lib/services/fields';
import { getFieldsValidations, getFieldValidation } from 'lib/services/fieldsValidations';
import { getParameter } from 'lib/services/parameters';
import { getValueList, searchValues, getList } from 'lib/services/valueLists';
import { showErrorToast } from 'lib/errors';
import { getFieldTypesList } from 'lib/literals';

import ForceForm, { validate, flatList } from 'components/ForceForm';
import HyperDrawer from 'components/HyperDrawer';
import Tour from 'components/Tour';
import FormValueList from 'shards/ValueLists/FormValueList';
import FormFieldsValidations from 'shards/FieldsValidations/FormFieldsValidations';
import FormSmartFieldConfig from '../FormSmartFieldConfig';
import { SmartFieldsConfirmModal, SmartFieldsValuesSection } from '../components';
import { useSmartFieldsValues, useSmartFieldsToggle } from '../hooks';
import { getDescription } from 'utils/custom';
import { executeSandbox } from 'utils/sandbox';
import {
    DUPLICATES_SECTION_SCHEMA,
    DUPLICATES_SECTION_SCHEMA_DISABLED,
    ENABLED_DUPLICATE_ENTITIES,
    formatDynamicFields,
} from '../lib';

import styles from './styles';
const useStyles = createUseStyles(styles, 'FormTab');

const FORM_SCHEMA = {
    context: {
        fields: {
            valueListRelated: {
                table: null,
            },
            dynamicFieldValues: {
                table: null,
            },
            integrationField: {
                table: 'tblIntegrationValues',
            },
        },
    },
    properties: {
        fieldName: {
            readOnly: true,
        },
        apiName_v4: {
            readOnly: true,
        },
        literalKey: {
            readOnly: true,
        },
        customValidation: {
            type: ['integer'],
        },
        defaultValue: {
            type: ['string'],
        },
        visible: {
            type: ['boolean'],
        },
        mandatory: {
            type: ['boolean'],
        },
        readOnly: {
            type: ['boolean'],
        },
        dynamicField: {
            type: ['string'],
        },
        dynamicFieldValues: {
            type: ['array'],
            items: { type: 'integer' },
        },
        integrationField: {
            type: ['integer'],
        },
        integrationFieldName: {
            type: ['string'],
        },
    },
    layout: {
        id: 'updateStandardField',
        uiGrid: 'linearVertical',
        composition: [
            {
                schemaId: 'entity',
                label: 'cfm_label_entity',
                uiType: 'list',
                placeholder: 'label_selectone',
            },
            {
                schemaId: 'description',
                uiType: 'text',
                label: 'cfm_label_field_name',
                isReadOnly: true,
            },
            {
                schemaId: 'fieldName',
                label: 'cfm_label_db_name',
                uiType: 'text',
                placeholder: 'cfm_placeholder_type_value',
            },
            {
                schemaId: 'apiName_v4',
                label: 'cfm_label_apiv4_name',
                uiType: 'text',
                placeholder: 'cfm_placeholder_type_value',
            },
            {
                schemaId: 'literalKey',
                label: 'cfm_label_literal_key',
                uiType: 'text',
                placeholder: 'cfm_placeholder_type_value',
            },
            {
                schemaId: 'type',
                label: 'cfm_label_data_type',
                uiType: 'list',
            },
            {
                schemaId: 'valueListRelated',
                label: 'cfm_label_related_table',
                uiType: 'list',
                placeholder: 'cfm_placeholder_type_value',
                composition: [
                    {
                        schemaId: 'buttonvalue',
                        uiType: 'button',
                        label: 'cfm_label_description',
                    },
                ],
                isHidden: false,
            },
            {
                schemaId: 'customValidation',
                label: 'cfm_label_validation',
                uiType: 'list',
                placeholder: 'label_selectone',
                noOptionsPlaceholder: 'cfm_helptext_no_validations',
            },
            {
                schemaId: 'defaultValue',
                label: 'cfm_label_default_value',
                uiType: 'text',
                placeholder: 'cfm_placeholder_type_value',
            },
            {
                schemaId: 'generalSettings',
                uiType: 'checkboxGroup',
                uiGrid: 'linearVertical',
                label: 'cfm_label_general_config',
                composition: [
                    {
                        id: 'visible',
                        label: 'cfm_label_visible',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        id: 'mandatory',
                        label: 'cfm_label_mandatory',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        id: 'readOnly',
                        label: 'cfm_label_readonly',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                ],
            },
            {
                schemaId: 'comment',
                label: 'cfm_label_internal_comment',
                uiType: 'textarea',
                placeholder: 'cfm_label_internal_comment_desc',
            },
            {
                schemaId: 'integrationSection',
                uiType: 'section',
                label: 'label_cfm_integration_configuration',
                composition: [
                    {
                        schemaId: 'integrationField',
                        label: 'label_integration_direction',
                        uiType: 'list',
                        placeholder: 'label_selectone',
                    },
                    {
                        schemaId: 'integrationFieldName',
                        label: 'cfm_label_integrated_field',
                        uiType: 'text',
                    },
                ],
            },
            {
                schemaId: 'dynamicsSection',
                uiType: 'section',
                label: 'label_sf_config_dynamism',
                composition: [
                    {
                        schemaId: 'dynamicField',
                        label: 'cfm_label_parent_dynamic_name',
                        uiType: 'list',
                        placeholder: 'label_selectone',
                        emptyPlaceholder: 'cfm_placeholder_type_value',
                    },
                    {
                        schemaId: 'dynamicFieldValues',
                        label: 'cfm_label_parent_dynamic_value',
                        uiType: 'multiList',
                        isFuzzy: true,
                        placeholder: 'label_selectone',
                        emptyPlaceholder: 'cfm_label_all',
                    },
                    {
                        schemaId: 'useSmartFields',
                        label: 'label_sf_enable_option',
                        uiType: 'switch',
                        helpTip: 'label_sf_disclaimer',
                        isHidden: true,
                    },
                    {
                        schemaId: 'smartFieldsValuesSection',
                        uiType: 'section',
                        label: 'label_sf_values',
                        isHidden: true,
                        composition: [
                            {
                                schemaId: 'smartFieldsValuesList',
                                uiType: 'custom',
                                isHidden: true,
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

const initialState = {
    data: {},
    originalData: {},
    fields: [],
    entities: [],
    tabs: [],
    dynamicFields: [],
    crudType: null,
    errors: {},
    isOpen: false,
    isLoading: true,
    isSaving: false,
    validationsCode: {},
    valueLists: {},
    tables: {},
};

const loadedValues = [];

const entitiesLiterals = {
    Empresas: 'cfm_label_accounts',
    Contactos: 'cfm_label_contacts',
    Expedientes: 'cfm_label_opportunities',
    Gestiones: 'cfm_label_activities',
    tblAgenda: 'cfm_label_event_task',
    tblSalesOrders: 'cfm_label_salesorders',
    Productos: 'cfm_label_products',
    tblUsuarios: 'cfm_label_users',
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                originalData: action.data,
                data: {
                    ...action.data,
                    dynamicFieldValues: action.data.dynamicFieldValues?.map((el) => ({
                        value: el.id,
                        label: el.value,
                    })),
                },
                entityOptions: action.entityOptions,
                validationCode: action.validationCode,
                errors: {},
                isOpen: true,
                isLoading: false,
                valueLists: action.valueLists || {},
                fields: action.fields || [],
                tables: {
                    ...state.tables,
                    dynamicFieldValues: action.data?.dynamicField?.valueListRelated,
                },
            };
        case 'close':
            return { ...state, isSaving: false, isOpen: false };
        case 'changeData':
            return { ...state, data: action.data };
        case 'changeDataAndTables':
            const finalData = {
                ...state.data,
                ...action.data,
                dynamicFieldValues:
                    action.data?.dynamicField &&
                    action.data?.dynamicField?.valueListRelated ===
                        state.data?.dynamicField?.valueListRelated
                        ? action.data.dynamicFieldValues
                        : undefined,
            };
            const finalObj = {
                ...state,
                data: finalData,
                tables: { ...state.tables, dynamicFieldValues: action.tablesDynamicFieldValues },
            };
            return finalObj;
        case 'changeValueList':
            loadedValues.push({
                label: action.data.name,
                value: action.data.name,
                isEditable: action.data.isEditable,
                id: action.data.id || action.data.idList,
            });
            state = {
                ...state,
                data: {
                    ...state.data,
                    valueListRelated: {
                        label: action.data.name,
                        value: action.data.name,
                        isEditable: action.data.isEditable,
                        id: action.data.id || action.data.idList,
                    },
                },
                loadedValues,
            };
            return state;
        case 'changeValidation':
            return {
                ...state,
                data: {
                    ...state.data,
                    customValidation: action.data,
                },
            };
        case 'updateSmartFieldConfig':
            const existingConfigs = state.data.smartFields || [];
            const filteredConfigs = existingConfigs.filter((c) => c.value !== action.valueId);
            return {
                ...state,
                data: {
                    ...state.data,
                    smartFields: [...filteredConfigs, ...action.configs],
                },
            };
        case 'setErrors':
            return { ...state, isSaving: false, errors: action.errors };
        case 'setSaving':
            return {
                ...state,
                isSaving: action.value,
            };
        case 'setValidationCode':
            return {
                ...state,
                validationsCode: { ...state.validationsCode, [action.validation]: action.code },
            };
        default:
            throw new Error('No action provided');
    }
}

const notAllowEntities = [
    'coferta',
    'tblcampaignsstatuses',
    'tblcampaignstypes',
    'tblsales',
    'tblsurveysvalues',
    'tblsalesorderslines',
    'tblformstemplate',
    'tblsalesorders',
];

const allowedForCustomValidations = ['Expedientes', 'Empresas', 'Contactos', 'tblAgenda'];

const allowedTypesOfValidation = ['text', 'int', 'percentage', 'decimal', 'currency', 'datetime'];

const allowedForRelevantFields = ['Empresas'];

const FormStandardField = ({
    onRef,
    refreshList,
    setActiveRowId,
    idSetupRole,
    processDataBeforeSave,
    entityLogName,
    entityLogSubmodule,
    overrides,
    onOpenUpgradeAdminInfo,
    fieldsData,
    isSage = false,
}) => {
    const {
        t,
        language,
        isImplementator,
        permissions,
        logEvent,
        deviceCache,
        setDeviceCache,
        overridePermissions,
        overrideTours,
        customOnCloseTour,
        hideCreateValidation,
        fieldsToHide = [],
        isWeb,
        onClickGoToAdmin,
    } = useWeb5Context({
        overrides,
    });
    const [state, dispatch] = useReducer(reducer, initialState);
    const [stackIndex, setStackIndex] = useState(1);
    const formValueLists = useRef();
    const formValidations = useRef();
    const formSmartFieldConfig = useRef();
    const schema = useRef(JSON.parse(JSON.stringify(FORM_SCHEMA)));

    const dumbUser = idSetupRole ? [8, 9].includes(idSetupRole) : false;
    const classes = useStyles();

    // ========================================
    // SMARTFIELDS HOOKS
    // ========================================
    const {
        valueListItems,
        hasMore,
        loadMore,
        isLoading: isLoadingValues,
    } = useSmartFieldsValues({
        valueListRelated: state.data?.dynamicField?.valueListRelated,
        language,
        useSmartFields: state.data?.useSmartFields,
    });

    const {
        showConfirmModal: showSmartFieldsConfirmModal,
        handleToggle: handleToggleSmartFields,
        handleConfirm: handleConfirmSmartFields,
        handleCancelConfirm: handleCancelSmartFieldsConfirm,
    } = useSmartFieldsToggle({
        currentData: state.data,
        onToggleSmartFields: (data) => dispatch({ type: 'changeData', data }),
    });

    // ========================================
    // SMARTFIELDS HANDLERS
    // ========================================
    const stateDataRef = useRef();
    stateDataRef.current = state.data;

    const handleConfigureValue = useCallback(
        (valueId) => {
            setStackIndex((prev) => prev + 1);
            const valueName = valueListItems.find((item) => item.value === valueId)?.label;
            formSmartFieldConfig.current.open({
                valueId,
                valueName,
                fieldType: stateDataRef.current?.type?.value,
                fieldConfig: stateDataRef.current,
                existingConfigs: stateDataRef.current?.smartFields || [],
            });
        },
        [valueListItems],
    );

    const handleSaveSmartFieldConfig = useCallback((valueId, configs) => {
        dispatch({ type: 'updateSmartFieldConfig', valueId, configs });
    }, []);

    const renderSmartFieldsValuesList = useMemo(
        () => (
            <SmartFieldsValuesSection
                valueListItems={valueListItems}
                onConfigureValue={handleConfigureValue}
                hasMore={hasMore}
                onLoadMore={loadMore}
                isLoading={isLoadingValues}
                valueListRelated={state.data?.dynamicField?.valueListRelated}
            />
        ),
        [
            valueListItems,
            handleConfigureValue,
            hasMore,
            loadMore,
            isLoadingValues,
            state.data?.dynamicField?.valueListRelated,
        ],
    );

    useEffect(() => {
        onRef &&
            onRef({
                open({ idDb, entity, entities }) {
                    if (!idDb) return;
                    Promise.all([
                        getParameter('implementation', 'CheckDuplicateFields'),
                        getStandardField(idDb),
                        getList({ list: 'tblIntegrationValues' }),
                    ])
                        .then(([parameter, { data }, integrationValuesResponse]) => {
                            // Checking duplicate param is disabled
                            const duplicateParamDisabled =
                                parameter?.data?.value === '0' || !parameter?.data?.value;

                            const layoutComposition = schema.current.layout.composition.filter(
                                (item) =>
                                    ![
                                        'generalSettings',
                                        'checkDuplicatesSection',
                                        'comment',
                                        'integrationSection',
                                        'dynamicsSection',
                                    ].includes(item.schemaId),
                            );

                            const generalSettings = schema.current.layout.composition.find(
                                (item) => item.schemaId === 'generalSettings',
                            );
                            const internalComment = schema.current.layout.composition.find(
                                (item) => item.schemaId === 'comment',
                            );
                            const integrationSection = schema.current.layout.composition.find(
                                (item) => item.schemaId === 'integrationSection',
                            );
                            const dynamicsSection = schema.current.layout.composition.find(
                                (item) => item.schemaId === 'dynamicsSection',
                            );
                            const generalSettingsComposition = [...generalSettings.composition];

                            const mandatory = generalSettingsComposition.find(
                                (item) => item.id === 'mandatory',
                            );
                            const readOnly = generalSettingsComposition.find(
                                (item) => item.id === 'readOnly',
                            );

                            mandatory.isDisabled = data && !data.visible && !data.mobileVisibility;
                            readOnly.isDisabled = data && !data.visible && !data.mobileVisibility;
                            const relatedOptions = entities
                                ?.filter(
                                    (f) => notAllowEntities.indexOf(f.name?.toLowerCase()) === -1,
                                )
                                .map((efEntity) => {
                                    let { name: value } = efEntity;
                                    const customLiteral =
                                        t(entitiesLiterals[efEntity.name]) || null;
                                    return {
                                        label: getDescription({
                                            tab: efEntity,
                                            dumbUser,
                                            customLiteral,
                                        }),
                                        value,
                                        id: efEntity?.id,
                                        isEditable: efEntity?.isEditable,
                                    };
                                });
                            const boolType = data.defaultValue === '1';
                            const finalDefaultValue =
                                (data.type === 'list' || data.type === 'multiValue') &&
                                data.defaultValue
                                    ? Number(data.defaultValue)
                                    : data.defaultValue;

                            const properties = data.readOnlyMask
                                ? {
                                      ...schema.current.properties,
                                      visible: {
                                          ...schema.current.properties.visible,
                                          readOnly: data.readOnlyMask.isVisible,
                                      },
                                      mandatory: {
                                          ...schema.current.properties.mandatory,
                                          readOnly: data.readOnlyMask.isMandatory,
                                      },
                                      readOnly: {
                                          ...schema.current.properties.readOnly,
                                          readOnly: data.readOnlyMask.isReadOnly,
                                      },
                                  }
                                : schema.current.properties;

                            const newSchema = {
                                ...schema.current,
                                layout: {
                                    ...schema.current.layout,
                                    composition: [
                                        ...layoutComposition,
                                        {
                                            ...generalSettings,
                                            composition: generalSettingsComposition,
                                        },
                                        {
                                            ...internalComment,
                                            isHidden: overridePermissions,
                                        },
                                        integrationSection,
                                    ],
                                },
                                properties,
                            };

                            newSchema.layout.composition.push({
                                ...(duplicateParamDisabled
                                    ? DUPLICATES_SECTION_SCHEMA_DISABLED
                                    : DUPLICATES_SECTION_SCHEMA),
                            });

                            // Add SmartFields section (dynamicsSection) at the end
                            newSchema.layout.composition.push(dynamicsSection);

                            newSchema.layout.composition[3].options = relatedOptions;

                            schema.current = newSchema;

                            const entityOptions = entities?.map((option) => {
                                const customLiteral = t(entitiesLiterals[option.name]) || null;
                                return {
                                    label: getDescription({ tab: option, dumbUser, customLiteral }),
                                    value: option.name,
                                };
                            });

                            const fields = fieldsData?.[entity]?.fields || null;

                            const allDynamicFields =
                                fields && entity
                                    ? formatDynamicFields(
                                          data?.fieldName || '',
                                          entity,
                                          fields,
                                          language,
                                          t,
                                          fieldsData,
                                      )
                                    : [];

                            const integrationFieldOptions =
                                integrationValuesResponse?.data?.map((item) => ({
                                    value: item.id,
                                    label: item.description,
                                })) || [];

                            const valueLists = {
                                dynamicField: allDynamicFields.filter(
                                    (field) => field.value !== data?.fieldName,
                                ),
                                integrationField: integrationFieldOptions,
                            };

                            const finalData = {
                                ...data,
                                type: getFieldTypesList(t)?.find((el) => el.value === data.type),
                                description: t(data.description),
                                entity: { label: entity, value: entity },
                                defaultValue: data.type === 'bit' ? boolType : finalDefaultValue,
                                valueListRelated: relatedOptions?.find(
                                    (el) =>
                                        el.value.toLowerCase() ===
                                            data.valueListRelated.toLowerCase() ||
                                        el.label === data.valueListRelated,
                                ),
                                customValidation: data.customValidation
                                    ? {
                                          value: data.customValidation.id,
                                          label: data.customValidation.value,
                                      }
                                    : null,
                                dynamicField: valueLists.dynamicField?.find(
                                    (el) => el.value === data.dynamicField,
                                ),
                                dynamicFieldValues: data.dynamicFieldValues,
                                smartFields: data?.smartFields || [],
                                useSmartFields: data?.smartFields?.length > 0,
                                integrationField: integrationFieldOptions?.find(
                                    (el) => el.value === data.integrationField,
                                ),
                                integrationFieldName: data.integrationFieldName,
                            };

                            dispatch({
                                type: 'init',
                                data: finalData,
                                entityOptions,
                                valueLists,
                                fields,
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            dispatch({ type: 'setErrors', errors: error });
                            showErrorToast(t, error);
                        });
                },
            });
    }, [isImplementator, onRef, t, dumbUser, overridePermissions, fieldsData, language]);

    const onSaveValueListForm = useCallback((data) => {
        dispatch({ type: 'changeValueList', data });
    }, []);

    const onSaveFieldValidationForm = useCallback((data) => {
        dispatch({ type: 'changeValidation', data: { value: data.id, label: data.name } });
    }, []);

    const onClickButtonList = useCallback(
        (value) => {
            setStackIndex(stackIndex + 1);
            formValueLists.current.open({
                id: value.id,
                isEditable: value.isEditable,
            });
        },
        [stackIndex],
    );

    const customValidationsActions = useMemo(
        () => [
            {
                label: t('cfm_action_create_validation'),
                onClick: () => {
                    setStackIndex(stackIndex + 1);
                    formValidations.current.open({ type: state.data.type.value });
                },
            },
        ],
        [stackIndex, state.data, t],
    );

    const onSearchCustomValidation = useCallback(
        (search = '') =>
            getFieldsValidations({
                page: 0,
                name: search,
                fieldType: state.data.type?.value,
                count: 999999999,
            }).then(({ data }) => data.map((el) => ({ value: el.id, label: el.name }))),
        [state.data.type],
    );

    const onSearchValues = useCallback(
        (text = 'a') => {
            return searchValues({
                entity: state?.tables?.dynamicFieldValues,
                text,
                lang: language,
            })
                .then(({ data }) => {
                    return data.map((item) => ({
                        label: item.description,
                        value: item.id,
                    }));
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        [language, state?.tables?.dynamicFieldValues],
    );

    const modifyFieldProps = useCallback(
        (field) => {
            let newField = field;
            let newSchema = schema.current;

            const overrideIsHidden = (id) =>
                Array.isArray(fieldsToHide) && fieldsToHide.includes(id);

            switch (field.schemaId || field.id) {
                case 'entity':
                    newField = {
                        ...newField,
                        options: state?.entityOptions,
                        isReadOnly: true,
                        isHidden: overrideIsHidden('entity'),
                    };
                    break;
                case 'type':
                    newField = {
                        ...newField,
                        options: getFieldTypesList(t),
                        isReadOnly: true,
                    };

                    const fieldsBySchemaId = newSchema.layout.composition.reduce((acc, item) => {
                        if (item.schemaId) acc[item.schemaId] = item;
                        return acc;
                    }, {});

                    const {
                        valueListRelated: valueListRelatedField,
                        defaultValue: defaultValueField,
                    } = fieldsBySchemaId;

                    const configByType = {
                        datetime: () => {
                            defaultValueField.uiType = 'text';
                            valueListRelatedField.isHidden = true;
                            defaultValueField.isHidden = true;
                        },
                        bit: () => {
                            defaultValueField.uiType = 'checkbox';
                            valueListRelatedField.isHidden = true;
                            defaultValueField.isHidden = false;
                        },
                        int: () => {
                            defaultValueField.uiType = 'integer';
                            defaultValueField.placeholder = 'cfm_placeholder_numeric';
                            valueListRelatedField.isHidden = true;
                            defaultValueField.isHidden = false;
                        },
                        multivalue: () => {
                            defaultValueField.uiType = state.data?.valueListRelated
                                ? 'list'
                                : 'text';
                            defaultValueField.placeholder = 'label_selectone';
                            defaultValueField.valueObjectNeeded =
                                state.data?.valueListRelated?.value;
                            valueListRelatedField.isHidden = false;
                        },
                        list: () => {
                            defaultValueField.uiType = state.data?.valueListRelated
                                ? 'list'
                                : 'text';
                            defaultValueField.placeholder = 'label_selectone';
                            defaultValueField.valueObjectNeeded =
                                state.data?.valueListRelated?.value;
                            valueListRelatedField.isHidden = false;
                        },
                        percentage: () => {
                            defaultValueField.uiType = 'number';
                            defaultValueField.placeholder = 'cfm_placeholder_percentage';
                            valueListRelatedField.isHidden = true;
                            defaultValueField.isHidden = false;
                        },
                        decimal: () => {
                            defaultValueField.uiType = 'number';
                            defaultValueField.placeholder = 'cfm_placeholder_decimal';
                            valueListRelatedField.isHidden = true;
                            defaultValueField.isHidden = false;
                        },
                        currency: () => {
                            defaultValueField.uiType = 'number';
                            defaultValueField.placeholder = 'cfm_placeholder_percentage';
                            valueListRelatedField.isHidden = true;
                            defaultValueField.isHidden = false;
                        },
                        text: () => {
                            defaultValueField.uiType = 'text';
                            defaultValueField.placeholder = 'cfm_placeholder_type_value';
                            defaultValueField.isHidden = false;
                            valueListRelatedField.isHidden = true;
                        },
                        default: () => {
                            valueListRelatedField.isHidden = true;
                            defaultValueField.isHidden = true;
                        },
                    };

                    const typeValue = state.data?.type?.value;
                    (configByType[typeValue] || configByType.default)();

                    break;
                case 'description':
                    newField = {
                        ...newField,
                        hint: t('cfm_label_field_name_help'),
                    };
                    break;
                case 'apiName_v4':
                    newField = {
                        ...newField,
                        hint: t('cfm_label_apiv4_name_help'),
                        isHidden: overrideIsHidden('apiName_v4'),
                    };
                    break;
                case 'literalKey':
                    newField = {
                        ...newField,
                        hint: t('cfm_label_literal_key_help'),
                        isHidden: overrideIsHidden('literalKey'),
                    };
                    break;
                case 'fieldName':
                    newField = {
                        ...newField,
                        isReadOnly: true,
                        hint: t('cfm_label_db_name_help'),
                        isHidden: overrideIsHidden('fieldName'),
                    };
                    break;
                case 'valueListRelated':
                    const { loadedValues } = state;
                    newField = {
                        ...newField,
                        isHidden: !state.data?.valueListRelated,
                        isReadOnly: true,
                        loadedValues,
                    };
                    if (overridePermissions || permissions?.valueList?.update) {
                        newField = {
                            ...newField,
                            onClickButtonList,
                        };
                    }
                    newSchema.context = {
                        ...newSchema.context,
                        fields: {
                            ...newSchema.context.fields,
                            defaultValue: { table: state.data?.valueListRelated?.value },
                        },
                    };
                    break;
                case 'customValidation':
                    newField = {
                        ...newField,
                        actions:
                            !hideCreateValidation &&
                            permissions?.fieldsValidations?.create &&
                            customValidationsActions,
                        loadOptionsWithFuzzy: onSearchCustomValidation,
                        isFuzzy: true,
                        overrides: {
                            'react-select': {
                                defaultOptions: true,
                                openMenuOnClick: true,
                            },
                        },
                        isHidden:
                            overrideIsHidden('customValidation') ||
                            !state.data?.type?.value ||
                            !allowedTypesOfValidation.includes(state.data?.type?.value) ||
                            (!overridePermissions &&
                                !permissions?.fieldsValidations?.rootFieldsValidations) ||
                            !allowedForCustomValidations.includes(state.data?.entity?.value),
                    };
                    break;
                case 'defaultValue':
                    newField = {
                        ...newField,
                        hint: t('cfm_label_default_value_help'),
                        key: state.data?.valueListRelated?.value,
                        isHidden:
                            overrideIsHidden('defaultValue') ||
                            (state.data?.type?.value === 'list' &&
                                state.data?.valueListRelated == null),
                    };
                    break;
                case 'checkDuplicatesSection':
                    newField = {
                        ...newField,
                        isHidden:
                            overrideIsHidden('checkDuplicatesSection') ||
                            !(
                                ENABLED_DUPLICATE_ENTITIES.includes(state?.data?.entity?.value) &&
                                state.data?.type?.value === 'text'
                            ),
                        className: 'fs-form__section-checkDuplicates',
                    };
                    break;
                case 'generalSettings':
                    newField = fieldsToHide.length
                        ? {
                              ...newField,
                              inputs: newField.inputs.map((input) =>
                                  overrideIsHidden(input.name)
                                      ? { ...input, isHidden: true }
                                      : input,
                              ),
                          }
                        : newField;
                    break;
                case 'relevantFields':
                    newField = {
                        ...newField,
                        isHidden: !allowedForRelevantFields.includes(state.data?.entity?.value),
                    };
                    break;
                case 'dynamicField':
                    newField = {
                        ...newField,
                        options: state.valueLists?.dynamicField || [],
                        isReadOnly: !state.data?.entity,
                        hint: t('cfm_label_tooltip_dynamic_fields'),
                        isHidden: overrideIsHidden('dynamicField'),
                    };
                    break;
                case 'integrationSection':
                    newField = {
                        ...newField,
                        isHidden: !isSage,
                    };
                    break;
                case 'integrationField':
                    newField = {
                        ...newField,
                        options: state.valueLists?.integrationField || [],
                        isReadOnly: !permissions?.extraFields?.updateIntegrationField,
                    };
                    break;
                case 'integrationFieldName':
                    newField = {
                        ...newField,
                        isReadOnly: !permissions?.extraFields?.updateIntegrationField,
                    };
                    break;
                case 'dynamicFieldValues':
                    const hasSmartFields =
                        !!state.data?.smartFields && state.data.smartFields.length > 0;
                    const useSmartFields = state.data?.useSmartFields;
                    newField = {
                        ...newField,
                        isReadOnly:
                            !state.data?.entity ||
                            !state.data?.dynamicField ||
                            hasSmartFields ||
                            useSmartFields,
                        isRequired: state.data?.dynamicField && !useSmartFields,
                        isDisabled: hasSmartFields || useSmartFields,
                        table: state.tables?.dynamicFieldValues,
                        loadOptionsWithFuzzy: onSearchValues,
                        key: state.tables?.dynamicFieldValues,
                        isHidden: overrideIsHidden('dynamicFieldValues'),
                    };
                    break;
                case 'dynamicsSection':
                    newField = {
                        ...newField,
                        isHidden: overrideIsHidden('dynamicsSection'),
                    };
                    break;
                case 'useSmartFields':
                    const isDynamicFieldMultivalue =
                        state.data?.dynamicField?.type === 'multivalue';
                    newField = {
                        ...newField,
                        isHidden: overrideIsHidden('useSmartFields'),
                        isReadOnly: !state.data?.dynamicField?.value || isDynamicFieldMultivalue,
                    };
                    break;
                case 'smartFieldsValuesSection':
                    newField = {
                        ...newField,
                        isHidden: !state.data?.useSmartFields || !state.data?.dynamicField?.value,
                    };
                    break;
                case 'smartFieldsValuesList':
                    newField = {
                        ...newField,
                        isHidden: !state.data?.useSmartFields || !state.data?.dynamicField?.value,
                        Field: () => renderSmartFieldsValuesList,
                    };
                    break;
                default:
                    break;
            }
            schema.current = newSchema;
            return newField;
        },
        [
            fieldsToHide,
            state,
            t,
            overridePermissions,
            permissions?.valueList?.update,
            permissions?.fieldsValidations?.create,
            permissions?.fieldsValidations?.rootFieldsValidations,
            permissions?.extraFields?.updateIntegrationField,
            hideCreateValidation,
            customValidationsActions,
            onSearchCustomValidation,
            onSearchValues,
            onClickButtonList,
            renderSmartFieldsValuesList,
            isSage,
        ],
    );

    const validateDefaultValue = useCallback(
        ({ isValid, errors, cleanData }) => {
            const validation = cleanData.customValidation?.value || null;
            const getValidationCode = () => {
                if (!validation) return Promise.resolve({ code: null });
                if (state.validationsCode[validation])
                    return Promise.resolve({ code: state.validationsCode[validation] });
                return getFieldValidation(validation);
            };

            return getValidationCode()
                .then(({ code }) => {
                    if (!code) return null;
                    if (validation && !state.validationsCode[validation])
                        dispatch({
                            type: 'setValidationCode',
                            validation,
                            code,
                        });
                    return executeSandbox(cleanData.defaultValue, code);
                })
                .then((error) => ({
                    isValid: isValid && !error?.length,
                    errors: {
                        ...errors,
                        defaultValue: error?.length ? { message: error } : errors.defaultValue,
                    },
                    cleanData,
                }));
        },
        [state.validationsCode],
    );

    const onSave = useCallback(() => {
        validate(state.data, schema.current)
            .then(validateDefaultValue)
            .then(({ isValid, errors, cleanData }) => {
                if (!isValid) {
                    dispatch({ type: 'setErrors', errors });
                } else {
                    dispatch({ type: 'setSaving', value: true });
                    // Tracking 'Link validation'
                    if (
                        !state.originalData.customValidation &&
                        cleanData.customValidation &&
                        (entityLogSubmodule === 'accountsTab' ||
                            entityLogSubmodule === 'contactsTab' ||
                            entityLogSubmodule === 'opportunitiesTab' ||
                            entityLogSubmodule === 'eventsAndTasksTab')
                    )
                        logEvent({
                            event: entityLogName,
                            submodule: entityLogSubmodule,
                            functionality: 'linkValidation',
                        });

                    // Tracking 'Save'
                    logEvent({
                        event: entityLogName,
                        submodule: entityLogSubmodule,
                        functionality: state.crudType === 'update',
                    });
                    let finalCleanData = flatList(cleanData, schema.current);
                    let boolType = finalCleanData.defaultValue ? '1' : '0';
                    let finalDefaultValue =
                        finalCleanData.type === 'bit' ? boolType : `${finalCleanData.defaultValue}`;
                    finalCleanData = {
                        ...finalCleanData,
                        defaultValue: finalCleanData.defaultValue ? finalDefaultValue : '',
                        smartFields: state.data.smartFields || [],
                    };

                    if (processDataBeforeSave) {
                        finalCleanData = processDataBeforeSave(finalCleanData);
                    }
                    updateStandardField(state.data.id, finalCleanData)
                        .then(() => {
                            refreshList && refreshList();
                            setActiveRowId(null);
                            dispatch({ type: 'close' });
                            showToast({
                                type: 'success',
                                text: t('cfm_success_update_field'),
                                title: t('cfm_label_success'),
                                useDefaultCloseButton: true,
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            showErrorToast(t, error);
                            dispatch({ type: 'setErrors', errors: error });
                        });
                }
            })
            .catch((err) => {
                console.error(err);
            });
    }, [
        state.data,
        state.originalData.customValidation,
        state.crudType,
        validateDefaultValue,
        entityLogSubmodule,
        logEvent,
        entityLogName,
        processDataBeforeSave,
        refreshList,
        setActiveRowId,
        t,
    ]);

    const locales = useMemo(
        () => ({
            title: t('cfm_title_edit_standard_field'),
            cancel: t('cfm_action_cancel'),
            save: t('cfm_action_save'),
        }),
        [t],
    );

    const onCancel = useCallback(() => {
        setActiveRowId(null);
        dispatch({ type: 'close' });
    }, [setActiveRowId]);

    const onChange = useCallback(
        (data, field) => {
            switch (field) {
                case 'generalSettings':
                    const generalSettings = schema.current.layout.composition.find(
                        (item) => item.schemaId === 'generalSettings',
                    );
                    const generalSettingsComposition = [...generalSettings.composition];

                    const mandatory = generalSettingsComposition.find(
                        (item) => item.id === 'mandatory',
                    );
                    const readOnly = generalSettingsComposition.find(
                        (item) => item.id === 'readOnly',
                    );

                    mandatory.isDisabled = data && !data.visible && !data.mobileVisibility;
                    readOnly.isDisabled = data && !data.visible && !data.mobileVisibility;

                    const composition = schema.current.layout.composition.map((item) => {
                        if (item.schemaId === 'generalSettings') {
                            return {
                                ...generalSettings,
                                composition: generalSettingsComposition,
                            };
                        }
                        return item;
                    });

                    const newSchema = {
                        ...schema.current,
                        layout: {
                            ...schema.current.layout,
                            composition,
                        },
                    };
                    if (!data.visible) {
                        data.mandatory = false;
                        data.readOnly = false;
                    }
                    schema.current = newSchema;
                    dispatch({ type: 'changeData', data });
                    break;
                case 'relevantFields':
                    if (entityLogSubmodule === 'accountsTab')
                        logEvent({
                            event: entityLogName,
                            submodule: entityLogSubmodule,
                            functionality: 'featuredField',
                        });
                    break;
                case 'customValidation':
                    dispatch({ type: 'changeData', data });
                    break;
                case 'defaultValue':
                    if (data?.fieldName === 'ratio') {
                        if (data?.defaultValue > 10) data.defaultValue = 10;
                        else if (data?.defaultValue < 0) data.defaultValue = 0;
                    }
                    dispatch({ type: 'changeData', data });
                    break;
                case 'useSmartFields':
                    handleToggleSmartFields(data.useSmartFields);
                    break;
                case 'dynamicField':
                    const tablesDynamicFieldValues = data?.dynamicField?.valueListRelated;
                    const isNewDynamicFieldMultivalue = data?.dynamicField?.type === 'multivalue';
                    const isDynamicFieldCleared = !data?.dynamicField;
                    schema.current.context = {
                        ...schema.current.context,
                        fields: {
                            ...schema.current.context.fields,
                            dynamicFieldValues: { table: data?.dynamicField?.valueListRelated },
                        },
                    };
                    const dynamicFieldData =
                        isNewDynamicFieldMultivalue || isDynamicFieldCleared
                            ? {
                                  ...data,
                                  useSmartFields: false,
                                  smartFields: [],
                              }
                            : data;
                    dispatch({
                        type: 'changeDataAndTables',
                        data: dynamicFieldData,
                        tablesDynamicFieldValues,
                    });
                    break;
                case 'dynamicFieldValues':
                    let finalData = {
                        ...data,
                        dynamicFieldValues: data?.dynamicFieldValues?.length
                            ? data?.dynamicFieldValues
                            : undefined,
                    };
                    dispatch({ type: 'changeData', data: finalData });
                    break;

                default:
                    dispatch({ type: 'changeData', data });
                    break;
            }
        },
        [entityLogName, entityLogSubmodule, logEvent, handleToggleSmartFields],
    );

    const steps = [
        {
            selector: '.fs-form__section-checkDuplicates',
            title: t('cfm_label_promotion_duplicates'),
            description: t('cfm_label_promotion_duplicates_body'),
        },
    ];

    const onCloseTour = useCallback(() => {
        if (customOnCloseTour) {
            customOnCloseTour('duplicateFields');
        } else {
            const deviceCacheDuplicateFields = deviceCache?.duplicateFields || {};
            setDeviceCache({
                ...deviceCache,
                duplicateFields: { ...deviceCacheDuplicateFields, duplicateButton: true },
            });
        }
    }, [customOnCloseTour, deviceCache, setDeviceCache]);

    const shouldShowTour = useMemo(() => {
        // Duplicates is enabled
        if (
            ENABLED_DUPLICATE_ENTITIES.includes(state?.data?.entity?.value) &&
            state?.data?.type?.value === 'text'
        ) {
            if (overrideTours && overrideTours.hasOwnProperty('duplicateFields'))
                return !overrideTours.duplicateFields;
            const deviceCacheDuplicateFields = deviceCache?.duplicateFields || {};
            if (deviceCacheDuplicateFields?.duplicateButton) return false;
            return true;
        }
        return false;
    }, [
        deviceCache?.duplicateFields,
        overrideTours,
        state?.data?.entity?.value,
        state?.data?.type?.value,
    ]);

    const onClickAdvanced = useCallback(() => {
        if (onClickGoToAdmin) return onClickGoToAdmin();
        return onOpenUpgradeAdminInfo();
    }, [onClickGoToAdmin, onOpenUpgradeAdminInfo]);

    useLayoutEffect(() => {
        if (shouldShowTour) {
            setTimeout(() => {
                document
                    .querySelector(`.fs-form__section-checkDuplicates`)
                    ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }, 500);
        }
    }, [shouldShowTour]);

    const finalOverrides = useMemo(
        () => ({
            ...(overrides || {}),
            fieldsToHide: overrides?.valueListFieldsToHide || [],
            columnsToHide: overrides?.valueListColumnsToHide || [],
        }),
        [overrides],
    );

    return (
        <Fragment>
            <HyperDrawer
                isOpen={state.isOpen}
                locales={locales}
                isLoading={state.isLoading}
                isSaving={state.isSaving}
                onCancel={onCancel}
                onSave={onSave}
                stackIndex={stackIndex}
                data={state.data}
                originalData={state.originalData}
                className="fields__extra-field-form"
            >
                {shouldShowTour && <Tour steps={steps} onClose={onCloseTour} delay={1000} />}
                <ForceForm
                    values={state.data}
                    errors={state.errors}
                    onChange={onChange}
                    schema={schema.current}
                    mode="create"
                    className={classes.root}
                    stackIndex={stackIndex}
                    modifyFieldProps={modifyFieldProps}
                />
                {isWeb && (
                    <Button type="secondary" icon="settings" isFullWidth onClick={onClickAdvanced}>
                        {t('action_advanced_options')}
                    </Button>
                )}
            </HyperDrawer>
            <FormValueList
                onRef={(ref) => (formValueLists.current = ref)}
                onSave={onSaveValueListForm}
                onStack={setStackIndex}
                overrides={finalOverrides}
                isSage={isSage}
            />
            <FormFieldsValidations
                onRef={(ref) => (formValidations.current = ref)}
                onSave={onSaveFieldValidationForm}
                stackIndex={stackIndex}
                onStack={setStackIndex}
                overrides={overrides}
            />
            <FormSmartFieldConfig
                onRef={(ref) => (formSmartFieldConfig.current = ref)}
                onSave={handleSaveSmartFieldConfig}
                onStack={setStackIndex}
            />
            <SmartFieldsConfirmModal
                isOpen={showSmartFieldsConfirmModal}
                onConfirm={handleConfirmSmartFields}
                onCancel={handleCancelSmartFieldsConfirm}
            />
        </Fragment>
    );
};

FormStandardField.propTypes = {
    onRef: PropTypes.func.isRequired,
    refreshList: PropTypes.func,
    setActiveRowId: PropTypes.func,
    idSetupRole: PropTypes.number,
    processDataBeforeSave: PropTypes.func,
    entityLogName: PropTypes.string,
    entityLogSubmodule: PropTypes.string,
    overrides: PropTypes.object,
    onOpenUpgradeAdminInfo: PropTypes.func,
    fieldsData: PropTypes.object,
};

export default memo(FormStandardField);
